"use client";

import React, { useState, useRef } from "react";
import { Input, Button, Spin, Alert } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useDoctorVerifyCode from "../../hooks/useDoctorVerifyCode";
import icon1 from "../../../assets/images/logomedilink.svg";
import chooseImage from "../../../assets/icons/tswira.png";

import "./VerifCode.css";
import "../../../authPatient/pages/SignupPatient/SignupPatient.css";

// doit correspondre à la durée de transition CSS (0.5s)
const SWAP_DURATION = 500;
// clé localStorage partagée entre toutes les pages auth (patient/doctor)
const SWAP_STORAGE_KEY = "authHeroOnLeft";
// layout d'origine de cette page : hero à gauche (classe signup-doctor)
const DEFAULT_HERO_ON_LEFT = true;

// Découpe un titre en "reste du texte" + "dernier mot en accent"
const renderAccentTitle = (text) => {
  const words = text.split(" ");
  const lastWord = words.pop();
  return (
    <>
      {words.length > 0 ? words.join(" ") + " " : ""}
      <span className="signup-form-title-accent">{lastWord}</span>
    </>
  );
};

export default function VerifCodeDoctor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || "").startsWith("ar");

  const {
    code,
    errors,
    isResending,
    countdown,
    setInputRef,
    handleChange,
    handleKeyDown,
    handlePaste,
    handleVerify,
    handleResend,
    maskedEmail,
    codeFilled,
    isVerified,
  } = useDoctorVerifyCode();

  const loading = isResending;
  const error = errors?.general || "";

  // ── état persistant de la position des cartes (hero à gauche ou à droite) ──
  const initialHeroOnLeft = (() => {
    if (typeof location.state?.heroOnLeft === "boolean") {
      return location.state.heroOnLeft;
    }
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? DEFAULT_HERO_ON_LEFT : stored === "true";
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);

  // ── refs + état pour le swap des 2 cards (form <-> hero) ──
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  const handleContinueAsPatient = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate("/LoginPatient", { state: { heroOnLeft: newHeroOnLeft } });
    }, SWAP_DURATION);
  };

  return (
    <div className={`signup-container signup-doctor-auth ${isRTL ? "is-rtl" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
      <div
        className={`signup-background signup-doctor ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form" onKeyDown={handleKeyDown}>
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {renderAccentTitle(t("auth.verifyAccountTitle"))}
              </h1>
              <p className="signup-form-subtitle">
                {t("auth.verifyCodeSentEmail")} <strong>{maskedEmail}</strong>
              </p>
            </div>

            <div className="signup-form-content">
              {loading && (
                <div className="vc-loader">
                  <Spin tip={t("commonn.loading")} />
                </div>
              )}

              {!isVerified && (
                <>
                  <div className="signup-form-group">
                    <label className="signup-form-label">{t("auth.verifyCode")}</label>

                    <div className="vc-code-inputs" onPaste={handlePaste}>
                      {code.map((digit, idx) => (
                        <Input
                          key={idx}
                          ref={(el) => setInputRef(idx, el)}
                          value={digit}
                          onChange={(e) => handleChange(e.target.value, idx)}
                          onKeyDown={handleKeyDown(idx)}
                          maxLength={1}
                          className={`vc-code signup-form-input ${error ? "vc-error" : ""}`}
                          placeholder="0"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          disabled={loading}
                        />
                      ))}
                    </div>

                    {error && (
                      <Alert className="vc-alert" type="error" message={error} showIcon />
                    )}
                  </div>

                  <Button
                    onClick={handleVerify}
                    className="signup-create-account-btn"
                    disabled={!codeFilled || loading}
                    type="primary"
                  >
                    <span>{loading ? t("commonn.loading") : t("auth.verifyCode")}</span>
                    <ArrowRightOutlined />
                  </Button>

                  <div className="vc-resend-section">
                    <p className="vc-resend-text">{t("auth.didntReceiveCode")}</p>
                    <Button
                      type="link"
                      disabled={countdown > 0 || loading}
                      className="vc-resend-btn"
                      onClick={handleResend}
                    >
                      {loading
                        ? t("commonn.loading")
                        : countdown > 0
                        ? t("auth.resendInSeconds", { count: countdown })
                        : t("auth.resendCode")}
                    </Button>
                  </div>

                  <div className="vc-back-login">
                    <Button
                      type="link"
                      onClick={() => navigate("/LoginDoctor")}
                      className="signup-login-link"
                      disabled={loading}
                    >
                      <ArrowLeftOutlined /> {t("auth.backToLogin")}
                    </Button>
                  </div>
                </>
              )}

              {isVerified && (
                <div className="signup-form-group">
                  <Alert
                    className="vc-alert vc-alert-success"
                    type="success"
                    message={t("auth.verificationSuccessRedirectDoctor")}
                    showIcon
                  />
                </div>
              )}
            </div>
          </div>
        </section>

        <section
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-glow" />
          <button
            type="button"
            className="signup-back-home signup-back-home-doctor signup-hero-back-btn"
            onClick={() => navigate("/")}
            aria-label={t("common.backToHome", "Back to home page")}
          >
            <ArrowLeftOutlined />
            <span>{t("common.backToHome", "Back to home page")}</span>
          </button>

          <div className="signup-hero-top-icon" aria-hidden="true">
            <img src={icon1} alt="" />
          </div>

          <div className="signup-hero-inner">
            <h1 className="signup-hero-title">
              {t("signup.createAccountHeroTitle", "Are you signing up as a patient?")}
            </h1>
            <p className="signup-hero-sub">
              {t("signup.createAccountHeroText", "This registration is for doctors only. To book appointments and access care, please create a patient account instead.")}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleContinueAsPatient}
            >
              <span>{t("signup.continueAsPatient", "Continue as a patient")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}